import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Page from '../components/Page';
import Iconify from '../components/Iconify';
import { getLiveZoneCameras } from '../api/liveZones';
import DashboardBreadcrumbLink from 'src/components/DashboardBreadcrumbLink';

const LIVE_VIDEO_OPACITY = 100;

export default function LiveHeatmap() {
  const clientId = Number(localStorage.getItem('client_id'));
  const hasAccess = clientId === 1444 || clientId === 1442;
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const playerRef = useRef(null);
  const isMaximized = isFullscreen || isExpanded;

  useEffect(() => {
    if (!hasAccess) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError('');
    getLiveZoneCameras(clientId).then((items) => {
      if (!active) return;
      setCameras(items);
      setCameraId((current) => items.some((item) => item.id === current)
        ? current : items[0]?.id || '');
    }).catch(() => {
      if (active) setError('Unable to load cameras. Please try again.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [clientId, hasAccess]);

  const camera = useMemo(
    () => cameras.find((item) => item.id === cameraId),
    [cameras, cameraId]
  );
  const streamSource = useMemo(() => {
    const streamBaseUrl = (process.env.REACT_APP_STREAM_URL || '')
      .trim()
      .replace(/\/+$/, '');
    if (!streamBaseUrl || !camera?.id) return '';
    return `${streamBaseUrl}/embed/${encodeURIComponent(camera.id)}`;
  }, [camera]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === playerRef.current);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keep maximize available when the browser does not support fullscreen.
  useEffect(() => {
    if (!isExpanded) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsExpanded(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExpanded]);

  const toggleMaximize = async () => {
    if (isExpanded) {
      setIsExpanded(false);
      return;
    }
    if (document.fullscreenElement === playerRef.current) {
      await document.exitFullscreen().catch(() => {});
      return;
    }
    try {
      if (!playerRef.current?.requestFullscreen) {
        setIsExpanded(true);
        return;
      }
      await playerRef.current.requestFullscreen();
    } catch {
      setIsExpanded(true);
    }
  };

  const placeholder = !hasAccess
    ? 'Live heatmap is available for client 1444 and 1442.'
    : error || (!cameras.length
      ? 'No cameras available for this client.'
      : 'Live stream URL is unavailable.');

  return (
    <Page title="Live heatmap">      
      <Container maxWidth="xxl" sx={{ py: 3 }}>
         <Box sx={{ mb: 4 }}>
                  <Typography variant="h4" sx={{ mb: 1 }}>Live Heatmap</Typography>
                  <Breadcrumbs separator="•" aria-label="breadcrumb">
                    <DashboardBreadcrumbLink sx={{ fontSize: '0.875rem' }} />
                    <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>Live Heatmap</Typography>
                  </Breadcrumbs>
        </Box>
        <Stack spacing={2}>
          <TextField
            select
            size="small"
            label="Camera"
            value={cameraId}
            disabled={loading || !cameras.length}
            onChange={(event) => setCameraId(event.target.value)}
            sx={{ width: { xs: '100%', sm: 350 } }}
          >
            {cameras.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>

          <Box
            ref={playerRef}
            sx={{
              position: isExpanded ? 'fixed' : 'relative',
              ...(isExpanded && { inset: 0, zIndex: (theme) => theme.zIndex.modal + 1 }),
              width: '100%',
              height: isExpanded ? '100dvh' : 'auto',
              aspectRatio: isExpanded ? 'auto' : '16 / 9',
              bgcolor: '#080d16',
              borderRadius: isMaximized ? 0 : 1,
              overflow: 'hidden',
              '&:fullscreen': { width: '100vw', height: '100dvh', aspectRatio: 'auto', borderRadius: 0 },
            }}
          >
            {!loading && streamSource ? (
              <iframe
                title="Live camera stream"
                src={streamSource}
                allow="autoplay; fullscreen"
                allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', border: 0, opacity: LIVE_VIDEO_OPACITY / 100 }}
              />
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={2}
                sx={{ height: '100%', color: '#fff', p: 3 }}
              >
                {loading ? <CircularProgress size={28} /> : <Iconify icon="eva:video-off-outline" width={40} />}
                <Typography>{loading ? 'Loading cameras…' : placeholder}</Typography>
              </Stack>
            )}

            <Tooltip title={isMaximized ? 'Restore player' : 'Maximize player'}>
              <IconButton
                aria-label={isMaximized ? 'Restore player' : 'Maximize player'}
                onClick={toggleMaximize}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 1,
                  bgcolor: '#101827dd',
                  color: '#fff',
                  '&:hover': { bgcolor: '#25324a' },
                }}
              >
                <Iconify icon={isMaximized ? 'mdi:fullscreen-exit' : 'mdi:fullscreen'} width={24} />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </Container>
    </Page>
  );
}
